/* JSON LD */
import { AggregateRating, LocalBusiness, MerchantReturnPolicy, Organization, WebSite, WithContext } from 'schema-dts'
import { JsonLd } from 'react-schemaorg'

import config from 'src/config'
import shopInfos from 'template/infos'
const {
  name, legalname,
  url, title, description, priceRange,
  email, telephone, fax:faxNumber, 

  street:streetAddress, 
  locality:addressLocality, postalcode:postalCode,
} = shopInfos
const shopUrl = url.replace(/\/$/, '')
const merchantReturnPolicyId = `${shopUrl}#merchant-return-policy`
const organizationId = `${shopUrl}#organization`
const localBusinessId = `${shopUrl}#local-business`

type ShopInfosWithReviews = typeof shopInfos & {
  googleRating?: string | number
  googleReviewCount?: string | number
  googleReviewHref?: string
}

const reviewInfos = shopInfos as ShopInfosWithReviews

const normalizeSchemaNumber = (value?: string | number) => {
  const normalized = String(value ?? '')
    .replace(/\+/g, '')
    .replace(',', '.')
    .replace(/[^0-9.]/g, '')

  const number = Number(normalized)
  return Number.isFinite(number) && number > 0 ? number : undefined
}

const googleRatingValue = normalizeSchemaNumber(reviewInfos.googleRating)
const googleReviewCount = normalizeSchemaNumber(reviewInfos.googleReviewCount)

const aggregateRating = googleRatingValue && googleReviewCount
  ? {
    '@type': 'AggregateRating',
    ratingValue: googleRatingValue,
    bestRating: 5,
    worstRating: 1,
    reviewCount: Math.floor(googleReviewCount),
  } as AggregateRating
  : undefined

const merchantReturnPolicyCountries = config.structuredData.returns.applicableCountries?.length
  ? config.structuredData.returns.applicableCountries
  : [config.structuredData.shipping.destinationCountry]

const merchantReturnPolicy = {
  '@context': 'https://schema.org',
  '@type': 'MerchantReturnPolicy',
  '@id': merchantReturnPolicyId,
  applicableCountry: merchantReturnPolicyCountries,
  returnPolicyCountry: config.structuredData.returns.returnPolicyCountry || 'DE',
  returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
  merchantReturnDays: config.structuredData.returns.merchantReturnDays,
  refundType: config.structuredData.returns.refundType,
  returnMethod: config.structuredData.returns.returnMethod,
  returnFees: config.structuredData.returns.returnFees,
  merchantReturnLink: `${shopUrl}/widerrufsbelehrung`,
} as WithContext<MerchantReturnPolicy>

/* ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */

export const SeoStructuredData: React.FC = () => <>

  {/* ----------------------------------------------------------------------- */}

  <JsonLd<WebSite> item={{
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${shopUrl}#website`,

    url,
    name: title,
    publisher: { '@id': organizationId },

    potentialAction: {
      '@type': 'SearchAction',
      target: `${url}suche?suche={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },

  } as WithContext<WebSite>} />

  {/* ----------------------------------------------------------------------- */}

  <JsonLd<Organization> item={{
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': organizationId,

    name: legalname || name || title,
    legalName: legalname,
    alternateName: name,
    url,
    email,
    telephone,
    sameAs: reviewInfos.googleReviewHref ? [reviewInfos.googleReviewHref] : undefined,
    address: {
      '@type': 'PostalAddress',
      streetAddress,
      addressLocality,
      postalCode,
      addressCountry: 'DE',
    },
  }} />

  {/* ----------------------------------------------------------------------- */}

  <JsonLd<LocalBusiness> item={{
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': localBusinessId,

    url,
    name: title,
    legalName: legalname,

    slogan: description,
    priceRange,
    parentOrganization: { '@id': organizationId },

    address: {
      '@type': 'PostalAddress',
      streetAddress,
      addressLocality,
      postalCode,
      addressCountry: 'DE',
      telephone,
      faxNumber,
    },

    telephone,
    faxNumber,
    email,
    sameAs: reviewInfos.googleReviewHref ? [reviewInfos.googleReviewHref] : undefined,
    aggregateRating,
    hasMerchantReturnPolicy: { '@id': merchantReturnPolicyId },

  } as WithContext<LocalBusiness>} />

  {/* ----------------------------------------------------------------------- */}

  <JsonLd<MerchantReturnPolicy> item={merchantReturnPolicy} />

  {/* ----------------------------------------------------------------------- */}

</>

/* ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
